import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LogIn, UserPlus } from 'lucide-react';
import axios from 'axios';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation tree — 9 main tabs with sub-tabs (per CRESA requirement)
const NAV_TREE = [
  { name: 'Accueil', path: '/' },
  {
    name: 'À propos',
    children: [
      { name: 'Présentation', path: '/a-propos#presentation' },
      { name: 'Mission & Vision', path: '/a-propos#mission' },
      { name: 'Équipe', path: '/a-propos#equipe' },
      { name: "Zones d'intervention", path: '/a-propos#zones' },
      { name: 'Partenaires', path: '/a-propos#partenaires' },
      { name: 'Carte', path: '/carte' },
    ],
  },
  {
    name: 'Nos actions',
    children: [
      { name: 'Projets', path: '/projets' },
      { name: 'Activités', path: '/activites' },
      { name: 'Visites', path: '/visiteur' },
      { name: 'Calendrier', path: '/calendrier' },
    ],
  },
  {
    name: 'Médias',
    children: [
      { name: 'Galerie', path: '/galerie' },
      { name: 'Blog', path: '/blog' },
      { name: 'Actualités', path: '/actualites' },
      { name: 'Témoignages', path: '/temoignages' },
    ],
  },
  {
    name: 'Ressources',
    children: [
      { name: 'Documents', path: '/documents' },
      { name: 'Rapports', path: '/documents?category=rapport' },
      { name: 'Publications', path: '/documents?category=etude' },
      { name: 'Formulaires', path: '/documents?category=brochure' },
    ],
  },
  {
    name: 'Opportunités',
    children: [
      { name: 'Formation', path: '/formations' },
      { name: 'Recrutement', path: '/recrutement' },
      { name: 'Volontariat', path: '/recrutement?category=volontariat' },
      { name: 'Stages', path: '/recrutement?category=stage' },
    ],
  },
  {
    name: 'Nous rejoindre',
    children: [
      { name: 'Devenir membre', path: '/register' },
      { name: 'Faire un don', path: '/dons' },
      { name: 'Demander un e-mail CRESA', path: '/demande-email-cresa' },
      { name: 'Espace employé CRESA', path: '/espace-employe' },
      { name: 'Contact', path: '/contact' },
    ],
  },
];

const slug = (s) => s
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '') // strip accents
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

function DesktopDropdown({ item, location, onNavigate }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const isActive = item.children?.some((c) => location.pathname === c.path.split('?')[0].split('#')[0]);

  return (
    <div ref={wrapperRef} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-tab-${slug(item.name)}`}
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex items-center gap-1 text-sm font-bold tracking-wide transition-all px-4 py-2.5 rounded-md border-2 ${
          isActive
            ? 'border-[#4A6B48] bg-[#4A6B48] text-white'
            : 'border-[#4A6B48]/30 text-[#4A6B48] hover:border-[#4A6B48] hover:bg-[#4A6B48]/10'
        }`}
      >
        {item.name}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[14rem] z-50"
          data-testid={`nav-dropdown-${slug(item.name)}`}
        >
          <div className="bg-white border-2 border-[#E5E3DB] rounded-lg shadow-xl overflow-hidden">
            {item.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                onClick={() => { setOpen(false); onNavigate?.(); }}
                data-testid={`nav-sub-${slug(item.name)}-${slug(child.name)}`}
                className="block px-4 py-2.5 text-sm text-[#2E332D] hover:bg-[#4A6B48]/5 hover:text-[#4A6B48] transition-colors font-medium border-b border-[#E5E3DB] last:border-b-0"
              >
                {child.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    setOpenMobileSection(null);
  };

  return (
    <nav className="nav-sticky" data-testid="main-navigation">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_ilot-communautaire/artifacts/zxm0khp6_LOGO%20CRESA.png"
              alt="CRESA ASBL"
              className="h-16 w-auto"
            />
          </Link>

          <div className="hidden xl:flex items-center gap-1.5 flex-wrap justify-end flex-1">
            {NAV_TREE.map((item) =>
              item.children ? (
                <DesktopDropdown key={item.name} item={item} location={location} />
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-tab-${slug(item.name)}`}
                  className={`text-sm font-bold tracking-wide transition-all px-4 py-2.5 rounded-md border-2 ${
                    location.pathname === item.path
                      ? 'border-[#4A6B48] bg-[#4A6B48] text-white'
                      : 'border-[#4A6B48]/30 text-[#4A6B48] hover:border-[#4A6B48] hover:bg-[#4A6B48]/10'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}

            {/* 8. Connexion / Mon espace */}
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin"
                    data-testid="nav-link-admin"
                    className="text-sm font-bold transition-all px-4 py-2.5 rounded-md border-2 border-[#D87D4A] text-[#D87D4A] hover:bg-[#D87D4A] hover:text-white">
                    Admin
                  </Link>
                )}
                {user.email?.toLowerCase().endsWith('@cresa-asbl.org') && (
                  <Link to="/espace-employe"
                    data-testid="nav-link-employee"
                    className="inline-flex items-center gap-1 text-sm font-bold px-4 py-2.5 rounded-md border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white">
                    Mon espace
                  </Link>
                )}
                <Link to="/membre"
                  data-testid="nav-link-member"
                  className="text-sm font-bold px-4 py-2.5 rounded-md border-2 border-[#4A6B48]/30 text-[#4A6B48] hover:border-[#4A6B48] hover:bg-[#4A6B48]/10">
                  Mon compte
                </Link>
                <button onClick={handleLogout}
                  data-testid="logout-button"
                  className="text-sm font-bold px-4 py-2.5 rounded-md bg-[#4A6B48] text-white hover:bg-[#3B5539]">
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-tab-connexion"
                className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2.5 rounded-md bg-[#4A6B48] text-white hover:bg-[#3B5539]">
                <LogIn size={14} /> Connexion
              </Link>
            )}

            {/* 9. Langue */}
            <LanguageSwitcher />
          </div>

          <div className="flex items-center gap-2 xl:hidden">
            <LanguageSwitcher />
            <button onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
              className="p-2 text-[#2E332D]">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile accordion */}
        {isOpen && (
          <div className="xl:hidden mt-4 pb-4 border-t border-[#E5E3DB] pt-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV_TREE.map((item) => {
                if (!item.children) {
                  return (
                    <Link key={item.path} to={item.path} onClick={closeMenu}
                      data-testid={`mobile-nav-${slug(item.name)}`}
                      className={`px-3 py-2.5 rounded-md text-base font-bold transition-colors ${
                        location.pathname === item.path
                          ? 'bg-[#4A6B48] text-white'
                          : 'text-[#2E332D] hover:bg-[#4A6B48]/10'
                      }`}>
                      {item.name}
                    </Link>
                  );
                }
                const isOpenSection = openMobileSection === item.name;
                return (
                  <div key={item.name} className="border-b border-[#E5E3DB]/50 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setOpenMobileSection(isOpenSection ? null : item.name)}
                      data-testid={`mobile-nav-${slug(item.name)}`}
                      aria-expanded={isOpenSection}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-base font-bold text-[#2E332D] hover:bg-[#4A6B48]/10 transition-colors"
                    >
                      <span>{item.name}</span>
                      <ChevronDown size={16} className={`transition-transform ${isOpenSection ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpenSection && (
                      <div className="ml-3 pl-3 border-l-2 border-[#4A6B48]/30 my-1 flex flex-col gap-0.5"
                        data-testid={`mobile-sub-${slug(item.name)}`}>
                        {item.children.map((child) => (
                          <Link key={child.path} to={child.path} onClick={closeMenu}
                            data-testid={`mobile-sub-link-${slug(item.name)}-${slug(child.name)}`}
                            className="px-3 py-2 rounded-md text-sm text-[#5C665A] hover:bg-[#4A6B48]/5 hover:text-[#4A6B48] transition-colors">
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="mt-3 pt-3 border-t border-[#E5E3DB] flex flex-col gap-2">
                {user ? (
                  <>
                    {user.role === 'admin' && (
                      <Link to="/admin" onClick={closeMenu}
                        data-testid="mobile-nav-admin"
                        className="px-3 py-2.5 rounded-md text-base font-bold text-[#D87D4A] border-2 border-[#D87D4A] text-center">
                        Admin
                      </Link>
                    )}
                    <Link to="/membre" onClick={closeMenu}
                      data-testid="mobile-nav-member"
                      className="px-3 py-2.5 rounded-md text-base font-bold text-[#4A6B48] border-2 border-[#4A6B48]/30 text-center">
                      Mon compte
                    </Link>
                    <button onClick={handleLogout}
                      data-testid="mobile-logout-button"
                      className="px-3 py-2.5 rounded-md text-base font-bold bg-[#4A6B48] text-white">
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMenu}
                      data-testid="mobile-nav-connexion"
                      className="inline-flex justify-center items-center gap-2 px-3 py-2.5 rounded-md text-base font-bold bg-[#4A6B48] text-white">
                      <LogIn size={16} /> Connexion
                    </Link>
                    <Link to="/register" onClick={closeMenu}
                      data-testid="mobile-nav-register"
                      className="inline-flex justify-center items-center gap-2 px-3 py-2.5 rounded-md text-base font-bold border-2 border-[#D87D4A] text-[#D87D4A]">
                      <UserPlus size={16} /> Devenir membre
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
